import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiFileText, 
  FiPlus, 
  FiEdit2, 
  FiTrash2, 
  FiDollarSign,
  FiCheckCircle,
  FiAlertCircle,
  FiFilter
} from 'react-icons/fi';
import './BaakProposal.css';

const BaakProposal = () => {
  const [proposals, setProposals] = useState([]);
  const [filteredProposals, setFilteredProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedProposal, setSelectedProposal] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({
    totalProposals: 0,
    totalAnggaran: 0,
    totalTerpakai: 0,
    totalSisa: 0,
    danaKemahasiswaan: 0
  });
  const [filterJenis, setFilterJenis] = useState('Semua');
  
  const [formData, setFormData] = useState({
    nomor_proposal: '',
    judul: '',
    jenis_proposal: 'Penelitian',
    pengusul: {
      nama: '',
      nim_nip: '',
      prodi: ''
    },
    tanggal_pengajuan: '',
    tanggal_disetujui: '',
    status: 'Disetujui',
    anggaran_disetujui: 0,
    deskripsi: '',
    keterangan: ''
  });

  const [invoiceForm, setInvoiceForm] = useState({
    nomor_invoice: '',
    tanggal_invoice: new Date().toISOString().split('T')[0],
    jenis_pengeluaran: 'Transportasi',
    deskripsi: '',
    jumlah: 0,
    status_pembayaran: 'Pending',
    keterangan: ''
  });

  const jenisProposalOptions = ['Penelitian', 'Pengabdian Masyarakat', 'Kegiatan Mahasiswa', 'Pengembangan SDM', 'Lainnya'];
  const jenisPengeluaranOptions = ['Transportasi', 'Konsumsi', 'Alat Tulis', 'Honor', 'Sewa Tempat', 'Publikasi', 'Lainnya'];

  const fetchProposals = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      if (!token) {
        toast.error('Token tidak ditemukan, silakan login kembali');
        return;
      }
      
      const response = await axios.get('/api/proposals?status=Disetujui', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Proposals fetched:', response.data);
      setProposals(response.data || []);
      setFilteredProposals(response.data || []);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      if (error.response) {
        // Server responded with error
        console.error('Error response:', error.response.data);
        toast.error(error.response.data?.message || 'Gagal mengambil data proposal');
      } else if (error.request) {
        // Request made but no response
        console.error('No response from server');
        toast.error('Server tidak merespons. Pastikan backend sudah berjalan');
      } else {
        // Error in request setup
        console.error('Error:', error.message);
        toast.error('Terjadi kesalahan: ' + error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        return;
      }
      
      const response = await axios.get('/api/proposals/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Stats fetched:', response.data);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      } else if (error.request) {
        console.error('No response from server for stats');
      }
      // Don't show toast for stats error, just log it
    }
  }, []);

  useEffect(() => {
    fetchProposals();
    fetchStats();
  }, [fetchProposals, fetchStats]);

  // Filter proposals by jenis
  useEffect(() => {
    if (filterJenis === 'Semua') {
      setFilteredProposals(proposals);
    } else {
      setFilteredProposals(proposals.filter(p => p.jenis_proposal === filterJenis));
    }
  }, [filterJenis, proposals]);

  const handleFilterChange = (jenis) => {
    setFilterJenis(jenis);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('pengusul.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        pengusul: {
          ...prev.pengusul,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleInvoiceInputChange = (e) => {
    const { name, value } = e.target;
    setInvoiceForm(prev => ({ ...prev, [name]: value }));
  };

  const openModal = (mode, proposal = null) => {
    setModalMode(mode);
    if (proposal) {
      setFormData({
        ...proposal,
        tanggal_pengajuan: proposal.tanggal_pengajuan ? new Date(proposal.tanggal_pengajuan).toISOString().split('T')[0] : '',
        tanggal_disetujui: proposal.tanggal_disetujui ? new Date(proposal.tanggal_disetujui).toISOString().split('T')[0] : ''
      });
    } else {
      setFormData({
        nomor_proposal: '',
        judul: '',
        jenis_proposal: 'Penelitian',
        pengusul: { nama: '', nim_nip: '', prodi: '' },
        tanggal_pengajuan: '',
        tanggal_disetujui: '',
        status: 'Disetujui',
        anggaran_disetujui: 0,
        deskripsi: '',
        keterangan: ''
      });
    }
    setShowModal(true);
  };

  const openInvoiceModal = async (proposal) => {
    setSelectedProposal(proposal);
    setInvoiceForm({
      nomor_invoice: '',
      tanggal_invoice: new Date().toISOString().split('T')[0],
      jenis_pengeluaran: 'Transportasi',
      deskripsi: '',
      jumlah: 0,
      status_pembayaran: 'Pending',
      keterangan: ''
    });
    
    // Fetch invoices for this proposal
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/proposals/${proposal._id}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      toast.error('Gagal mengambil data invoice');
    }
    
    setShowInvoiceModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (modalMode === 'create') {
        await axios.post('/api/proposals', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proposal berhasil ditambahkan');
      } else {
        await axios.put(`/api/proposals/${formData._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proposal berhasil diupdate');
      }
      
      setShowModal(false);
      fetchProposals();
      fetchStats();
    } catch (error) {
      console.error('Error saving proposal:', error);
      toast.error(error.response?.data?.message || 'Gagal menyimpan proposal');
    }
  };

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/proposals/${selectedProposal._id}/invoices`,
        invoiceForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Invoice berhasil dibuat');
      
      // Refresh invoices and proposals
      const response = await axios.get(`/api/proposals/${selectedProposal._id}/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoices(response.data || []);
      
      fetchProposals();
      fetchStats();
      
      // Reset form
      setInvoiceForm({
        nomor_invoice: '',
        tanggal_invoice: new Date().toISOString().split('T')[0],
        jenis_pengeluaran: 'Transportasi',
        deskripsi: '',
        jumlah: 0,
        status_pembayaran: 'Pending',
        keterangan: ''
      });
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.message || 'Gagal membuat invoice');
    }
  };

  const handleDeleteProposal = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus proposal ini?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/proposals/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Proposal berhasil dihapus');
        fetchProposals();
        fetchStats();
      } catch (error) {
        console.error('Error deleting proposal:', error);
        toast.error(error.response?.data?.message || 'Gagal menghapus proposal');
      }
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus invoice ini?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`/api/proposals/invoices/${invoiceId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Invoice berhasil dihapus');
        
        // Refresh invoices and proposals
        const response = await axios.get(`/api/proposals/${selectedProposal._id}/invoices`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvoices(response.data || []);
        
        fetchProposals();
        fetchStats();
      } catch (error) {
        console.error('Error deleting invoice:', error);
        toast.error(error.response?.data?.message || 'Gagal menghapus invoice');
      }
    }
  };

  const getJenisColor = (jenis) => {
    const colors = {
      'Penelitian': '#3b82f6',
      'Pengabdian Masyarakat': '#10b981',
      'Kegiatan Mahasiswa': '#f59e0b',
      'Pengembangan SDM': '#8b5cf6',
      'Lainnya': '#6b7280'
    };
    return colors[jenis] || '#6b7280';
  };

  return (
    <div className="baak-proposal-container">
      {/* Header */}
      <div className="proposal-header">
        <div className="header-backdrop"></div>
        <div className="header-content">
          <div className="header-left">
            <div className="icon-wrapper">
              <FiFileText className="header-icon" />
            </div>
            <div>
              <h1 className="header-title">Manajemen Proposal</h1>
              <p className="header-subtitle">Kelola proposal yang disetujui dan invoice pengeluaran</p>
            </div>
          </div>
          <button className="btn-add" onClick={() => openModal('create')}>
            <FiPlus /> Tambah Proposal
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-icon">
            <FiFileText />
          </div>
          <div className="stat-details">
            <p className="stat-label">Total Proposal</p>
            <h3 className="stat-value">{stats.totalProposals}</h3>
          </div>
        </div>
        <div className="stat-card green">
          <div className="stat-icon">
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <p className="stat-label">Total Anggaran</p>
            <h3 className="stat-value">{formatCurrency(stats.totalAnggaran)}</h3>
          </div>
        </div>
        <div className="stat-card cyan">
          <div className="stat-icon">
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <p className="stat-label">Dana Kemahasiswaan</p>
            <h3 className="stat-value">{formatCurrency(stats.danaKemahasiswaan)}</h3>
            <small className="stat-info">Dari pembayaran mahasiswa</small>
          </div>
        </div>
        <div className="stat-card orange">
          <div className="stat-icon">
            <FiAlertCircle />
          </div>
          <div className="stat-details">
            <p className="stat-label">Dana Terpakai</p>
            <h3 className="stat-value">{formatCurrency(stats.totalTerpakai)}</h3>
          </div>
        </div>
        <div className="stat-card purple">
          <div className="stat-icon">
            <FiCheckCircle />
          </div>
          <div className="stat-details">
            <p className="stat-label">Dana Tersisa</p>
            <h3 className="stat-value">{formatCurrency(stats.totalSisa)}</h3>
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="filter-card">
        <div className="filter-header">
          <FiFilter /> Filter Berdasarkan Jenis
        </div>
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filterJenis === 'Semua' ? 'active' : ''}`}
            onClick={() => handleFilterChange('Semua')}
          >
            Semua
          </button>
          {jenisProposalOptions.map(jenis => (
            <button 
              key={jenis}
              className={`filter-btn ${filterJenis === jenis ? 'active' : ''}`}
              onClick={() => handleFilterChange(jenis)}
            >
              {jenis}
            </button>
          ))}
        </div>
      </div>

      {/* Proposals List */}
      <div className="proposals-card">
        <div className="card-header">
          <h3><FiFileText /> Daftar Proposal Disetujui</h3>
          <span className="table-count">{filteredProposals.length} proposal</span>
        </div>

        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Memuat data proposal...</p>
          </div>
        ) : filteredProposals.length === 0 ? (
          <div className="empty-state">
            <FiFileText size={64} />
            <p>Belum ada proposal disetujui</p>
            <small>Klik tombol "Tambah Proposal" untuk menambahkan data</small>
          </div>
        ) : (
          <div className="proposals-grid">
            {filteredProposals.map((proposal) => (
              <div key={proposal._id} className="proposal-card">
                <div className="proposal-card-header">
                  <div className="proposal-info">
                    <span className="proposal-number">{proposal.nomor_proposal}</span>
                    <h4 className="proposal-title">{proposal.judul}</h4>
                    <span 
                      className="jenis-badge" 
                      style={{ backgroundColor: `${getJenisColor(proposal.jenis_proposal)}20`, color: getJenisColor(proposal.jenis_proposal) }}
                    >
                      {proposal.jenis_proposal}
                    </span>
                  </div>
                </div>

                <div className="proposal-card-body">
                  <div className="info-row">
                    <span className="info-label">Pengusul:</span>
                    <span className="info-value">{proposal.pengusul.nama}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Prodi:</span>
                    <span className="info-value">{proposal.pengusul.prodi}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Tanggal Disetujui:</span>
                    <span className="info-value">{formatDate(proposal.tanggal_disetujui)}</span>
                  </div>

                  <div className="budget-section">
                    <div className="budget-item">
                      <span className="budget-label">Anggaran Disetujui</span>
                      <span className="budget-value green">{formatCurrency(proposal.anggaran_disetujui)}</span>
                    </div>
                    <div className="budget-item">
                      <span className="budget-label">Dana Terpakai</span>
                      <span className="budget-value orange">{formatCurrency(proposal.anggaran_terpakai)}</span>
                    </div>
                    <div className="budget-item">
                      <span className="budget-label">Sisa Dana</span>
                      <span className="budget-value blue">{formatCurrency(proposal.anggaran_sisa)}</span>
                    </div>
                  </div>

                  <div className="progress-bar">
                    <div 
                      className="progress-fill" 
                      style={{ width: `${(proposal.anggaran_terpakai / proposal.anggaran_disetujui) * 100}%` }}
                    ></div>
                  </div>
                  <div className="progress-label">
                    {((proposal.anggaran_terpakai / proposal.anggaran_disetujui) * 100).toFixed(1)}% terpakai
                  </div>
                </div>

                <div className="proposal-card-footer">
                  <button 
                    className="btn-invoice" 
                    onClick={() => openInvoiceModal(proposal)}
                  >
                    <FiDollarSign /> Invoice
                  </button>
                  <button 
                    className="btn-edit" 
                    onClick={() => openModal('edit', proposal)}
                  >
                    <FiEdit2 />
                  </button>
                  <button 
                    className="btn-delete" 
                    onClick={() => handleDeleteProposal(proposal._id)}
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Form Proposal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'create' ? 'Tambah Proposal Baru' : 'Edit Proposal'}</h3>
              <button className="btn-close" onClick={() => setShowModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nomor Proposal *</label>
                    <input
                      type="text"
                      name="nomor_proposal"
                      value={formData.nomor_proposal}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Jenis Proposal *</label>
                    <select
                      name="jenis_proposal"
                      value={formData.jenis_proposal}
                      onChange={handleInputChange}
                      required
                    >
                      {jenisProposalOptions.map(jenis => (
                        <option key={jenis} value={jenis}>{jenis}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Judul Proposal *</label>
                    <input
                      type="text"
                      name="judul"
                      value={formData.judul}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Nama Pengusul *</label>
                    <input
                      type="text"
                      name="pengusul.nama"
                      value={formData.pengusul.nama}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>NIM/NIP *</label>
                    <input
                      type="text"
                      name="pengusul.nim_nip"
                      value={formData.pengusul.nim_nip}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Program Studi *</label>
                    <input
                      type="text"
                      name="pengusul.prodi"
                      value={formData.pengusul.prodi}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Anggaran Disetujui *</label>
                    <input
                      type="number"
                      name="anggaran_disetujui"
                      value={formData.anggaran_disetujui}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Tanggal Pengajuan *</label>
                    <input
                      type="date"
                      name="tanggal_pengajuan"
                      value={formData.tanggal_pengajuan}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Tanggal Disetujui</label>
                    <input
                      type="date"
                      name="tanggal_disetujui"
                      value={formData.tanggal_disetujui}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Deskripsi</label>
                    <textarea
                      name="deskripsi"
                      value={formData.deskripsi}
                      onChange={handleInputChange}
                      rows="3"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Keterangan</label>
                    <textarea
                      name="keterangan"
                      value={formData.keterangan}
                      onChange={handleInputChange}
                      rows="2"
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Batal
                </button>
                <button type="submit" className="btn-save">
                  <FiCheckCircle /> Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Invoice */}
      {showInvoiceModal && selectedProposal && (
        <div className="modal-overlay" onClick={() => setShowInvoiceModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Invoice Pengeluaran Dana</h3>
                <p className="modal-subtitle">{selectedProposal.judul}</p>
              </div>
              <button className="btn-close" onClick={() => setShowInvoiceModal(false)}>×</button>
            </div>

            <div className="modal-body">
              {/* Budget Info */}
              <div className="invoice-budget-info">
                <div className="budget-info-item">
                  <span className="label">Anggaran:</span>
                  <span className="value green">{formatCurrency(selectedProposal.anggaran_disetujui)}</span>
                </div>
                <div className="budget-info-item">
                  <span className="label">Terpakai:</span>
                  <span className="value orange">{formatCurrency(selectedProposal.anggaran_terpakai)}</span>
                </div>
                <div className="budget-info-item">
                  <span className="label">Tersisa:</span>
                  <span className="value blue">{formatCurrency(selectedProposal.anggaran_sisa)}</span>
                </div>
              </div>

              {/* Create Invoice Form */}
              <div className="invoice-form-section">
                <h4 className="section-title"><FiPlus /> Buat Invoice Baru</h4>
                <form onSubmit={handleCreateInvoice}>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Nomor Invoice *</label>
                      <input
                        type="text"
                        name="nomor_invoice"
                        value={invoiceForm.nomor_invoice}
                        onChange={handleInvoiceInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Tanggal Invoice *</label>
                      <input
                        type="date"
                        name="tanggal_invoice"
                        value={invoiceForm.tanggal_invoice}
                        onChange={handleInvoiceInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label>Jenis Pengeluaran *</label>
                      <select
                        name="jenis_pengeluaran"
                        value={invoiceForm.jenis_pengeluaran}
                        onChange={handleInvoiceInputChange}
                        required
                      >
                        {jenisPengeluaranOptions.map(jenis => (
                          <option key={jenis} value={jenis}>{jenis}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Jumlah *</label>
                      <input
                        type="number"
                        name="jumlah"
                        value={invoiceForm.jumlah}
                        onChange={handleInvoiceInputChange}
                        required
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>Deskripsi *</label>
                      <textarea
                        name="deskripsi"
                        value={invoiceForm.deskripsi}
                        onChange={handleInvoiceInputChange}
                        rows="2"
                        required
                      />
                    </div>

                    <div className="form-group full-width">
                      <label>Keterangan</label>
                      <textarea
                        name="keterangan"
                        value={invoiceForm.keterangan}
                        onChange={handleInvoiceInputChange}
                        rows="2"
                      />
                    </div>
                  </div>

                  <button type="submit" className="btn-create-invoice">
                    <FiPlus /> Buat Invoice
                  </button>
                </form>
              </div>

              {/* Invoice List */}
              <div className="invoice-list-section">
                <h4 className="section-title"><FiFileText /> Daftar Invoice</h4>
                {invoices.length === 0 ? (
                  <div className="empty-state-small">
                    <p>Belum ada invoice untuk proposal ini</p>
                  </div>
                ) : (
                  <div className="invoice-table">
                    <table>
                      <thead>
                        <tr>
                          <th>No. Invoice</th>
                          <th>Tanggal</th>
                          <th>Jenis</th>
                          <th>Deskripsi</th>
                          <th>Jumlah</th>
                          <th>Status</th>
                          <th>Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invoices.map((invoice) => (
                          <tr key={invoice._id}>
                            <td>{invoice.nomor_invoice}</td>
                            <td>{formatDate(invoice.tanggal_invoice)}</td>
                            <td>{invoice.jenis_pengeluaran}</td>
                            <td>{invoice.deskripsi}</td>
                            <td className="amount">{formatCurrency(invoice.jumlah)}</td>
                            <td>
                              <span className={`status-badge ${invoice.status_pembayaran.toLowerCase()}`}>
                                {invoice.status_pembayaran}
                              </span>
                            </td>
                            <td>
                              <button 
                                className="btn-delete-small"
                                onClick={() => handleDeleteInvoice(invoice._id)}
                              >
                                <FiTrash2 />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowInvoiceModal(false)}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BaakProposal;
